import React from 'react';
import Card from '@mui/material/Card';
import Divider from '@mui/material/Divider';

import MDBox from 'components/MDBox';
import MDTypography from 'components/MDTypography';

function fmtTry(value) {
  const amount = Number(value);
  if (!Number.isFinite(amount)) return '-';
  return `${Math.round(amount).toLocaleString('tr-TR')} ₺`;
}

function fmtPercent(value) {
  const amount = Number(value);
  if (!Number.isFinite(amount) || amount <= 0) return '%0';
  return `%${amount.toLocaleString('tr-TR')}`;
}

function normalizePaymentMethod(value) {
  const raw = String(value ?? '')
    .trim()
    .toUpperCase();

  if (['CARD', 'CREDIT_CARD', 'DEBIT_CARD', 'POS'].includes(raw)) return 'CARD';
  if (['CASH', 'NAKIT'].includes(raw)) return 'CASH';
  if (['TRANSFER', 'BANK_TRANSFER', 'EFT', 'HAVALE', 'IBAN'].includes(raw)) return 'TRANSFER';
  return 'UNKNOWN';
}

function getMemberKey(row) {
  return String(row?.memberId ?? row?.participantId ?? '');
}

function getSessionKey(row) {
  return String(row?.sessionId ?? '');
}

function getPricingLabel(row) {
  const label = String(row?.appliedPricingLabel ?? '').trim();
  return label || 'Tanimsiz fiyat';
}

function calculateCommission({
  payrollAmountTry = 0,
  participantCount = 0,
  cardCount = 0,
  applyCardCommission = false,
  cardCommissionPercent = 0,
}) {
  const totalPayroll = Number(payrollAmountTry || 0);
  const totalParticipants = Number(participantCount || 0);
  const totalCard = Number(cardCount || 0);
  const percent = Number(cardCommissionPercent || 0);

  if (
    !applyCardCommission ||
    totalPayroll <= 0 ||
    totalParticipants <= 0 ||
    totalCard <= 0 ||
    percent <= 0
  ) {
    return {
      commissionAmountTry: 0,
      netPayrollAmountTry: totalPayroll,
    };
  }

  const perParticipantAmount = totalPayroll / totalParticipants;
  const cardBaseAmount = perParticipantAmount * totalCard;
  const commissionAmountTry = cardBaseAmount * (percent / 100);

  return {
    commissionAmountTry,
    netPayrollAmountTry: totalPayroll - commissionAmountTry,
  };
}

function buildSummary(rows = [], options = {}) {
  const applyCardCommission = Boolean(options.applyCardCommission);
  const cardCommissionPercent = Number(options.cardCommissionPercent || 0);
  const payrollType = options.payrollType || 'HOURLY';
  const monthlySalary = Number(options.monthlySalary || 0);

  const memberSet = new Set();
  const sessionMap = new Map();

  for (const row of rows || []) {
    const memberKey = getMemberKey(row);
    const sessionKey = getSessionKey(row);

    if (memberKey) memberSet.add(memberKey);
    if (!sessionKey) continue;

    if (!sessionMap.has(sessionKey)) {
      sessionMap.set(sessionKey, []);
    }
    sessionMap.get(sessionKey).push(row);
  }

  let totalCardPaymentCount = 0;
  let debtOpenSessionCount = 0;
  let includedSessionCount = 0;
  let totalCommissionTry = 0;
  let totalNetPayrollTry = 0;

  const pricingBreakdownMap = new Map();

  for (const rowsInSession of sessionMap.values()) {
    const leaderRow = rowsInSession.find((row) => row?.isPayrollLeader) || rowsInSession[0] || null;
    if (!leaderRow) continue;

    const participantCount = rowsInSession.length;
    const cardCount = rowsInSession.filter((item) => {
      const rawMethod = item?.paymentMethod ?? item?.lastPaymentMethod ?? '';
      return normalizePaymentMethod(rawMethod) === 'CARD';
    }).length;

    const commissionResult = calculateCommission({
      payrollAmountTry: leaderRow?.includedInPayroll ? leaderRow?.payrollAmountTry : 0,
      participantCount,
      cardCount,
      applyCardCommission,
      cardCommissionPercent,
    });

    totalCardPaymentCount += cardCount;
    totalCommissionTry += commissionResult.commissionAmountTry;
    totalNetPayrollTry += commissionResult.netPayrollAmountTry;

    if (leaderRow?.includedInPayroll) {
      includedSessionCount += 1;

      const label = getPricingLabel(leaderRow);
      const key = String(leaderRow?.pricingRuleKey || label);

      if (!pricingBreakdownMap.has(key)) {
        pricingBreakdownMap.set(key, {
          key,
          label,
          sessionCount: 0,
          totalTry: 0,
        });
      }

      const breakdown = pricingBreakdownMap.get(key);
      breakdown.sessionCount += 1;
      breakdown.totalTry += commissionResult.netPayrollAmountTry;
    }

    const hasDebt = rowsInSession.some((item) => {
      const debt = Number(item?.planDebtTry || 0);
      const payStatus = String(item?.planPayStatus || '').toUpperCase();
      return debt > 0 || ['UNPAID', 'PARTIAL', 'PENDING', 'OVERDUE'].includes(payStatus);
    });

    if (hasDebt) debtOpenSessionCount += 1;
  }

  const pricingBreakdowns = Array.from(pricingBreakdownMap.values())
    .map((item) => ({
      ...item,
      unitTry: item.sessionCount > 0 ? item.totalTry / item.sessionCount : 0,
    }))
    .sort((a, b) => b.totalTry - a.totalTry);

  return {
    totalMembers: memberSet.size,
    totalSessions: sessionMap.size,
    includedSessionCount,
    pricingBreakdowns,
    totalCardPaymentCount,
    debtOpenSessionCount,
    totalCommissionTry,
    totalNetPayrollTry: payrollType === 'MONTHLY' ? monthlySalary : totalNetPayrollTry,
    cardCommissionPercent,
  };
}

function SummaryRow({ label, value, strong = false, tone = 'default' }) {
  const colorMap = {
    default: 'text.primary',
    success: 'success.main',
    danger: 'error.main',
    muted: 'text.secondary',
  };

  return (
    <MDBox
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 1,
      }}
    >
      <MDTypography variant="caption" sx={{ color: 'text.secondary' }}>
        {label}
      </MDTypography>

      <MDTypography
        variant="caption"
        fontWeight={strong ? 'bold' : 'medium'}
        sx={{ color: colorMap[tone] || colorMap.default, textAlign: 'right' }}
      >
        {value}
      </MDTypography>
    </MDBox>
  );
}

function PricingBreakdownRow({ item }) {
  return (
    <MDBox
      sx={{
        py: 0.6,
        borderBottom: '1px dashed rgba(15,23,42,0.08)',
      }}
    >
      <MDTypography variant="caption" sx={{ color: 'text.primary', lineHeight: 1.55 }}>
        {item.label}: {item.sessionCount} seans = {fmtTry(item.totalTry)}
      </MDTypography>
      <MDTypography variant="caption" sx={{ color: 'text.secondary', display: 'block' }}>
        Seans ucreti: {fmtTry(item.unitTry)}
      </MDTypography>
    </MDBox>
  );
}

export default function PayrollSummaryCard({
  rows = [],
  applyCardCommission = false,
  cardCommissionPercent = 0,
  payrollType = 'HOURLY',
  monthlySalary = 0,
}) {
  const summary = React.useMemo(
    () =>
      buildSummary(rows, {
        applyCardCommission,
        cardCommissionPercent,
        payrollType,
        monthlySalary,
      }),
    [rows, applyCardCommission, cardCommissionPercent, payrollType, monthlySalary]
  );

  return (
    <Card
      sx={{
        borderRadius: '16px',
        border: '1px solid rgba(15,23,42,0.08)',
        boxShadow: 'none',
        overflow: 'hidden',
        height: '100%',
      }}
    >
      <MDBox sx={{ px: 1.2, py: 1 }}>
        <MDTypography variant="button" fontWeight="bold">
          Hakedis Ozeti
        </MDTypography>
      </MDBox>

      <Divider />

      <MDBox sx={{ p: 1.2, display: 'grid', gap: 0.9 }}>
        <SummaryRow label="Toplam uye" value={summary.totalMembers} strong />
        <SummaryRow label="Toplam seans" value={summary.totalSessions} />
        <SummaryRow label="Ucretlenen seans" value={summary.includedSessionCount} />

        <Divider />

        <MDBox sx={{ display: 'grid', gap: 0.2 }}>
          <MDTypography variant="caption" fontWeight="bold" sx={{ color: 'text.secondary' }}>
            Fiyatlandirma dagilimi
          </MDTypography>

          {summary.pricingBreakdowns.length ? (
            summary.pricingBreakdowns.map((item) => (
              <PricingBreakdownRow key={item.key} item={item} />
            ))
          ) : (
            <MDTypography variant="caption" sx={{ color: 'text.secondary' }}>
              Bu filtrelerde fiyatlandirilmis seans yok.
            </MDTypography>
          )}
        </MDBox>

        <Divider />

        <SummaryRow label="Kartli odeme adedi" value={summary.totalCardPaymentCount} />
        <SummaryRow
          label="Borc acik seans"
          value={summary.debtOpenSessionCount}
          tone={summary.debtOpenSessionCount > 0 ? 'danger' : 'default'}
        />
        <SummaryRow
          label="Toplam kesinti orani"
          value={applyCardCommission ? fmtPercent(summary.cardCommissionPercent) : 'Uygulanmiyor'}
          tone={applyCardCommission ? 'danger' : 'muted'}
        />
        <SummaryRow
          label="Kesinti miktari"
          value={fmtTry(summary.totalCommissionTry)}
          strong
          tone="danger"
        />

        <Divider />

        {payrollType === 'MONTHLY' ? (
          <>
            <SummaryRow label="Aylik maas" value={fmtTry(monthlySalary)} strong tone="success" />
            <Divider />
          </>
        ) : null}

        <MDBox
          sx={{
            p: 1,
            borderRadius: '12px',
            border: '1px solid rgba(34,197,94,0.2)',
            bgcolor: 'rgba(34,197,94,0.08)',
          }}
        >
          <MDTypography variant="caption" sx={{ color: 'text.secondary', display: 'block' }}>
            Toplam net hakedis
          </MDTypography>
          <MDTypography variant="h6" fontWeight="bold" sx={{ color: 'success.main' }}>
            {fmtTry(summary.totalNetPayrollTry)}
          </MDTypography>
        </MDBox>
      </MDBox>
    </Card>
  );
}
